import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { usePostHog } from '@posthog/react';
import { capture, RESET_PASSWORD_PAGE_EVENT } from '../analytics/posthog';
import styles from './ResetPassword.module.css';
import BrandLogo from './BrandLogo';
import config, { logger } from '../config';
import { getApiHeaders } from '../utils/api';
import { validatePasswordStrength, getStrengthColor, getStrengthText, getRequirementColor, getStrengthGuidance } from '../utils/helper';

const ResetPassword = () => {
  const navigate = useNavigate();
  const posthog = usePostHog();
  const [searchParams] = useSearchParams();
  const [formData, setFormData] = useState({
    newPassword: '',
    confirmPassword: ''
  });
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [passwordStrength, setPasswordStrength] = useState(null);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    // Clear error when user starts typing
    if (error) {
      setError('');
    }

    // Check password strength when new password field changes
    if (name === 'newPassword') {
      const strength = validatePasswordStrength(value);
      setPasswordStrength(strength);
    }
  };

  const togglePasswordVisibility = (field) => {
    if (field === 'newPassword') {
      setShowNewPassword(!showNewPassword);
    } else if (field === 'confirmPassword') {
      setShowConfirmPassword(!showConfirmPassword);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    capture(posthog, RESET_PASSWORD_PAGE_EVENT, { action: 'submit_new_password' });

    // Validate passwords match
    if (formData.newPassword !== formData.confirmPassword) {
      setError('Passwords do not match');
      return;
    }

    setIsLoading(true);
    setError('');

    try {
      const response = await fetch(`${config.API_BASE_URL}${config.API_ENDPOINTS.RESET_PASSWORD}`, {
        method: 'POST',
        headers: getApiHeaders({ 'Content-Type': 'application/json' }),
        body: JSON.stringify({
          email: searchParams.get('email'),
          verification_code: searchParams.get('code'),
          new_password: formData.newPassword
        }),
      });

      const data = await response.json();

      // Check for success response
      if (data.success) {
        setSuccess(true);

        // Show success message and redirect to login after 2 seconds
        setTimeout(() => {
          navigate('/login');
        }, 2000);
      } else {
        // Handle other error responses from API
        let errorMessage = data.message || 'Password reset failed. Please try again.';

        // Handle validation errors if they exist
        if (data.validation_errors && Array.isArray(data.validation_errors)) {
          errorMessage = data.validation_errors.join('\n');
        }

        setError(errorMessage);
      }
    } catch (err) {
      logger.error('Password reset error:', err);
      setError('Network error. Please check your connection and try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleBackToLogin = () => {
    capture(posthog, RESET_PASSWORD_PAGE_EVENT, { action: 'back_to_login' });
    navigate('/login');
  };

  return (
    <main className={styles['reset-container']} id="reset-password-main" aria-label="Reset password">
      <div className={styles['reset-content']}>
        <div className={styles['logo-wrap']}>
          <BrandLogo alt="Richuel Logo" />
        </div>

        <div className={styles['reset-form-container']}>
          <h1 className={styles['reset-title']}>Reset password?</h1>

          <div className={styles['reset-instructions']}>
            <p>Enter your new password and confirm it</p>
          </div>

          {error && (
            <div
              key={error}
              id="reset-error"
              className={styles['error-message']}
              role="alert"
              aria-live="polite"
            >
              {error.split('\n').map((errorLine, index) => (
                <div key={index}>{errorLine}</div>
              ))}
            </div>
          )}

          {success && (
            <div
              key="reset-success"
              id="reset-success"
              className={styles['success-message']}
              role="status"
              aria-live="polite"
            >
              Password reset successful! Redirecting to login...
            </div>
          )}

          <form className={styles['reset-form']} onSubmit={handleSubmit} aria-label="Set new password">
            <div className={styles['form-group']}>
              <label htmlFor="reset-new-password">New Password</label>
              <div className={styles['password-input-container']}>
                <input
                  type={showNewPassword ? 'text' : 'password'}
                  id="reset-new-password"
                  name="newPassword"
                  placeholder="Enter here"
                  value={formData.newPassword}
                  onChange={handleInputChange}
                  required
                  disabled={isLoading}
                  autoComplete="new-password"
                  aria-describedby={[error && 'reset-error', formData.newPassword && passwordStrength && 'reset-strength'].filter(Boolean).join(' ') || undefined}
                />
                <button
                  type="button"
                  className={styles['password-toggle']}
                  onClick={() => togglePasswordVisibility('newPassword')}
                  disabled={isLoading}
                  aria-label={showNewPassword ? 'Hide new password' : 'Show new password'}
                >
                  <svg viewBox="0 0 24 24" width="20" height="20" fill="currentColor" aria-hidden>
                    {showNewPassword ? (
                      <path d="M12 4.5C7 4.5 2.73 7.61 1 12c1.73 4.39 6 7.5 11 7.5s9.27-3.11 11-7.5c-1.73-4.39-6-7.5-11-7.5zM12 17c-2.76 0-5-2.24-5-5s2.24-5 5-5 5 2.24 5 5-2.24 5-5 5zm0-8c-1.66 0-3 1.34-3 3s1.34 3 3 3 3-1.34 3-3-1.34-3-3-3z" />
                    ) : (
                      <path d="M12 7c2.76 0 5 2.24 5 5 0 .65-.13 1.26-.36 1.83l2.92 2.92c1.51-1.26 2.7-2.89 3.43-4.75-1.73-4.39-6-7.5-11-7.5-1.4 0-2.74.25-3.98.7l2.16 2.16C10.74 7.13 11.35 7 12 7zM2 4.27l2.28 2.28.46.46C3.08 8.3 1.78 10.02 1 12c1.73 4.39 6 7.5 11 7.5 1.55 0 3.03-.3 4.38-.84l.42.42L19.73 22 21 20.73 3.27 3 2 4.27zM7.53 9.8l1.55 1.55c-.05.21-.08.43-.08.65 0 1.66 1.34 3 3 3 .22 0 .44-.03.65-.08l1.55 1.55c-.67.33-1.41.53-2.2.53-2.76 0-5-2.24-5-5 0-.79.2-1.53.53-2.2zm4.31-.78l3.15 3.15.02-.16c0-1.66-1.34-3-3-3l-.17.01z" />
                    )}
                  </svg>
                </button>
              </div>

              {/* Password Strength Indicator */}
              {formData.newPassword && passwordStrength && (
                <div id="reset-strength" className={styles['password-strength']} role="status" aria-live="polite">
                  <div className={styles['strength-text']} style={{ color: getStrengthColor(passwordStrength.strength) }}>
                    {getStrengthText(passwordStrength.strength)}
                  </div>
                  <div className={styles['strength-bar']}>
                    <div
                      className={styles['strength-fill']}
                      style={{
                        width: `${passwordStrength.percentage}%`,
                        backgroundColor: getStrengthColor(passwordStrength.strength)
                      }}
                    ></div>
                  </div>
                  <div className={styles['strength-guidance']}>
                    {getStrengthGuidance(passwordStrength.strength)}
                  </div>
                  <div className={styles['strength-requirements']}>
                    <div className={styles['requirement-item']}>
                      <span
                        className={styles['requirement-bullet']}
                        style={{ backgroundColor: getRequirementColor(passwordStrength.requirements?.uppercase?.fulfilled) }}
                      ></span>
                      <span className={styles['requirement-text']}>
                        {passwordStrength.requirements?.uppercase?.text}
                      </span>
                    </div>
                    <div className={styles['requirement-item']}>
                      <span
                        className={styles['requirement-bullet']}
                        style={{ backgroundColor: getRequirementColor(passwordStrength.requirements?.special?.fulfilled) }}
                      ></span>
                      <span className={styles['requirement-text']}>
                        {passwordStrength.requirements?.special?.text}
                      </span>
                    </div>
                    <div className={styles['requirement-item']}>
                      <span
                        className={styles['requirement-bullet']}
                        style={{ backgroundColor: getRequirementColor(passwordStrength.requirements?.numeric?.fulfilled) }}
                      ></span>
                      <span className={styles['requirement-text']}>
                        {passwordStrength.requirements?.numeric?.text}
                      </span>
                    </div>
                    <div className={styles['requirement-item']}>
                      <span
                        className={styles['requirement-bullet']}
                        style={{ backgroundColor: getRequirementColor(passwordStrength.requirements?.alpha?.fulfilled) }}
                      ></span>
                      <span className={styles['requirement-text']}>
                        {passwordStrength.requirements?.alpha?.text}
                      </span>
                    </div>
                    <div className={styles['requirement-item']}>
                      <span
                        className={styles['requirement-bullet']}
                        style={{ backgroundColor: getRequirementColor(passwordStrength.requirements?.length?.fulfilled) }}
                      ></span>
                      <span className={styles['requirement-text']}>
                        {passwordStrength.requirements?.length?.text}
                      </span>
                    </div>
                    <div className={styles['requirement-item']}>
                      <span
                        className={styles['requirement-bullet']}
                        style={{ backgroundColor: getRequirementColor(passwordStrength.requirements?.noSpaces?.fulfilled) }}
                      ></span>
                      <span className={styles['requirement-text']}>
                        {passwordStrength.requirements?.noSpaces?.text}
                      </span>
                    </div>
                  </div>
                </div>
              )}
            </div>

            <div className={styles['form-group']}>
              <label htmlFor="reset-confirm-password">Confirm New Password</label>
              <div className={styles['password-input-container']}>
                <input
                  type={showConfirmPassword ? 'text' : 'password'}
                  id="reset-confirm-password"
                  name="confirmPassword"
                  placeholder="Enter here"
                  value={formData.confirmPassword}
                  onChange={handleInputChange}
                  required
                  disabled={isLoading}
                  autoComplete="new-password"
                  aria-describedby={error ? 'reset-error' : undefined}
                />
                <button
                  type="button"
                  className={styles['password-toggle']}
                  onClick={() => togglePasswordVisibility('confirmPassword')}
                  disabled={isLoading}
                  aria-label={showConfirmPassword ? 'Hide confirm password' : 'Show confirm password'}
                >
                  <svg viewBox="0 0 24 24" width="20" height="20" fill="currentColor" aria-hidden>
                    {showConfirmPassword ? (
                      <path d="M12 4.5C7 4.5 2.73 7.61 1 12c1.73 4.39 6 7.5 11 7.5s9.27-3.11 11-7.5c-1.73-4.39-6-7.5-11-7.5zM12 17c-2.76 0-5-2.24-5-5s2.24-5 5-5 5 2.24 5 5-2.24 5-5 5zm0-8c-1.66 0-3 1.34-3 3s1.34 3 3 3 3-1.34 3-3-1.34-3-3-3z" />
                    ) : (
                      <path d="M12 7c2.76 0 5 2.24 5 5 0 .65-.13 1.26-.36 1.83l2.92 2.92c1.51-1.26 2.7-2.89 3.43-4.75-1.73-4.39-6-7.5-11-7.5-1.4 0-2.74.25-3.98.7l2.16 2.16C10.74 7.13 11.35 7 12 7zM2 4.27l2.28 2.28.46.46C3.08 8.3 1.78 10.02 1 12c1.73 4.39 6 7.5 11 7.5 1.55 0 3.03-.3 4.38-.84l.42.42L19.73 22 21 20.73 3.27 3 2 4.27zM7.53 9.8l1.55 1.55c-.05.21-.08.43-.08.65 0 1.66 1.34 3 3 3 .22 0 .44-.03.65-.08l1.55 1.55c-.67.33-1.41.53-2.2.53-2.76 0-5-2.24-5-5 0-.79.2-1.53.53-2.2zm4.31-.78l3.15 3.15.02-.16c0-1.66-1.34-3-3-3l-.17.01z" />
                    )}
                  </svg>
                </button>
              </div>
            </div>

            <button
              type="submit"
              className={styles['submit-button']}
              disabled={isLoading || passwordStrength?.strength !== 'very strong'}
              aria-busy={isLoading}
              aria-label={isLoading ? 'Submitting new password…' : 'Submit new password'}
            >
              {isLoading ? 'Submitting...' : 'Submit'}
            </button>
          </form>

          <div className={styles['back-to-login']}>
            <button
              type="button"
              onClick={handleBackToLogin}
              className={styles['back-button']}
              aria-label="Back to login page"
            >
              Back to Login
            </button>
          </div>
        </div>
      </div>
    </main>
  );
};

export default ResetPassword;